import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import Lottie from 'react-lottie';
import { Frame } from "scenejs";

import { EditorContext } from '../../index';

import { splitPageBySelection, toggleEditMode, updateWidgetProperty } from 'actions/editor';
import { Property } from '../../components/properties/';
import { SCROLL_EFFECTS } from 'constants/scrollEffects';
import { MENU_CONTAINER, LIVE_ELEMENT, PAGE, MENU_DRAGGABLE_CONTAINER } from 'constants/widgetTypes';
import { useChildrenWidgetSelected, usePageWidgetSelector, useWidgetChildrenSelector, useWidgetSelected, useAllowCreateLiveElement} from 'components/Editor/hooks/useEditorSession';
import { useGroupContainerSelector } from 'components/Editor/hooks/useEditorSession';

import { LiveElementCreationProperty } from '../../components/liveElements';
import { getBoundingRectFromLayout } from 'components/Editor/helper/layoutManager';
import { Background } from 'components/Editor/components/properties/background';
import { BackgroundView } from '../background';
import { Container } from '../core/container';

import StoryboardContext from 'components/Storyboard/context';

import { HOTSPOT } from 'constants/widgetTypes';
import Tooltip from 'components/Tooltip';
import { getMediaInfo } from 'actions/pageHealth';
import { getWidgetType } from 'components/Editor/helper/widget';
import { BrandComponentProperty } from 'components/Editor/components/brandElements';
import userRoleService from 'services/userRoles.service';
import { useMakeFrame } from 'components/Editor/hooks/useMakeFrame';
import { PAGE_CONTAIN_STORYBOARDS } from 'constants/storyboardType';

import styles from './menu.module.scss';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const MenuWidgetViewComponent = function MenuWidget({ className, style, widgetProps, widgetRef, children }) {
  
  const ref = widgetRef;

  const [menuWrapperStyle, setMenuWrapperStyle] = useState({})

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false);
  const translate = _.get(widgetProps, `properties.${breakpoint}.layout.transform.translate`);
  const parent = _.get(widgetProps, 'parent');
  const background = _.get(widgetProps, 'properties.desktop.background');

  const parentSelected = useWidgetSelected(editor, parent);
  const childrenSelection = useChildrenWidgetSelected(editor, id);
  let alignment = getContainerAlignment(widgetProps, breakpoint);
  
  // TODO: Need to fix the setTimeout issue
  useEffect(() => {
    let timer = setTimeout(() => {
      let translateY = _.get(widgetProps, `properties.${breakpoint}.layout.transform.translate`, '')?.split(",")?.[1] || '0px';
      ref.current.style.transform = `translate(0px, ${translateY})`;
    }, 0);

    return () => clearTimeout(timer);
  }, [breakpoint, ref, stretched, translate]);

  useEffect(() => {

    var sectionWidgetStyle = {
      ...style,
      ...getStyles(widgetProps, breakpoint)
    }
    setMenuWrapperStyle(sectionWidgetStyle)

  }, [breakpoint, widgetProps])

  return (
    <Container widgetProps={widgetProps} widgetRef={widgetRef}>
      <div
        id={id}
        ref={ref}
        className={cx(className, styles.menuSection, 'menu', {
          [styles.stretched]: stretched,
          [styles.parentSelected]: parentSelected,
          [styles.partialSelection]: childrenSelection.selected,
          [styles.mobile]: breakpoint === 'mobile'
        })}
        style={menuWrapperStyle}
        data-widget-id={id}
        data-widget-type={type}
        data-container={true}
        data-selectable={!childrenSelection.selected}
      >
        <BackgroundView background={background} />
        {React.Children.map(children,
          child => {
            var widgetType = _.get(child, 'props.widgetProps.type');
            
            if (widgetType === MENU_CONTAINER || widgetType === MENU_DRAGGABLE_CONTAINER) {
              return (
                <div style={widgetType === MENU_CONTAINER ? alignment : {
                  alignItems: 'normal',
                  justifyContent: 'normal'
                }} className={cx(styles.childContainer)}>
                  {
                    React.cloneElement(child, {
                      parentRef: ref,
                      stretched: stretched,
                      parentProps: widgetProps
                    }, null)
                  }
                </div>
              )
            }
            return (
              React.cloneElement(child, {
                parentRef: ref,
                stretched: stretched,
                parentProps: widgetProps
              }, null)
            );
          })
        }
      </div>
    </Container>
  )
};

const menuWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef', 'children'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const MenuWidgetView = React.memo(MenuWidgetViewComponent, menuWidgetEquals);

const MenuWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));
  const widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`, []));

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const id = _.get(props, 'uid');
  const background = _.get(props, 'properties.desktop.background');
  const isGroupContainer = useGroupContainerSelector(editorSession, props);
  const template = _.get(props, 'template.widget') != null;
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const { makeFrameShow, makeFramePageId } = useMakeFrame({ editorSession });

  const hasMenuContainer = useMemo(() => {
    const children = widgets.filter(item => item.parent === props.uid);
    return children.some(item => item.type === MENU_CONTAINER);
  }, [widgets, props.uid]);

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }
  const handleLiveElement = () => {
    setView('liveElements')
  }
  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const handleMakeFrame = () => {
    dispatch(splitPageBySelection(editorSession, makeFramePageId, [props.uid]));
  };

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => changeView('background')}>
        Change Background
      </Property.Action>
      <Property.Divider />
      {makeFrameShow && 
        <>
          <Property.Action className={styles.textButton} tooltip='Split segment: selection becomes a new frame' onClick={handleMakeFrame}>
            Make Frame
          </Property.Action>
          <Property.Divider />
        </>
      }
      {hasMenuContainer &&
        <>
          <Property.Action className={styles.iconButton} tooltip='Alignment' onClick={() => changeView('style')}>
            <i className={cx("cleverstory-customize", styles.menuIcons)} />
          </Property.Action>
          <Property.Divider />
        </>
      }
      {entityType !== "LIVE_ELEMENT" &&
        <Property.Action className={styles.iconButton} tooltip='Scroll Effects' onClick={() => changeView('scroll')}>
          <i className={cx("cleverstory-scroll", styles.menuIcons)} />
        </Property.Action>
      }
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip='Stretch' onClick={() => changeView('stretch')}>
        <i className={cx("cleverstory-stretch", styles.menuIcons)} />
      </Property.Action>
      <Property.Divider />
      {allowLiveElement &&
      <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={handleLiveElement}>
        <i className={cx("cleverstory-live-element", styles.menuIcons)} />
        </Property.Action>
      }
      {!template && brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {!editMode && view === 'background' &&
        <Background.Dialog id={id}
          widgetProps={props}
          value={background}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {!editMode && view === 'stretch' &&
        <StretchMenuDialog
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'style' &&
        <MenuStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {!editMode && view === 'scroll' &&
        <ScrollEffectDialog
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'MENUS'} widgets={[props]} onCancel={() => closePopup()} />
      }
    </>
  );
}

const MenuStyleEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widget, `properties.${breakpoint}`, {});
  const stretched = _.get(widget, 'properties.desktop.stretched');
  const widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`, []));
  let menuContainer = widgets.find(item => item.parent === id && item.type === MENU_CONTAINER);

  const maxVerticalMargin =  useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    const menuHeight = parseInt(_.get(properties, 'layout.height'));
    let menuContainer = widgets.find(item => item.parent === id && item.type === MENU_CONTAINER);

    if(menuContainer) {
      const childElement = document.getElementById(menuContainer.uid);

      return (menuHeight - parseInt(childElement?.clientHeight))
    }
    return menuHeight
  });

  const alignment = _.get(properties, 'alignment', {
    position: 'center',
    margin: breakpoint === 'desktop' ? 5 : 15
  });

  const verticalAlignment = _.get(properties, 'verticalAlignment', {
    position: 'center',
    margin: breakpoint === 'desktop' ? 5 : 15
  });

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleAlignmentChange = (value) => {
    const position = _.get(value, 'position');


    if (position === 'center') {
      value = {
        ...value,
        margin: 5
      }
    }

    var request = {
      ...properties,
      alignment:{
        ...alignment,
        ...value
      }
    }

    handlePropertyChange(request);
  }

  const handleVerticalAlignmentChange = (value) => {
    const position = _.get(value, 'position');


    if (position === 'center') {
      value = {
        ...value,
        margin: 5
      }
    }

    var request = {
      ...properties,
      verticalAlignment:{
        ...verticalAlignment,
        ...value
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Alignment'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property name="Menu Container Alignment" nameStyle={styles.propertyName} className={styles.buttonAlignmentProperty}>
          <MenuContainerHorizontalAlignment
            value={alignment}
            breakpoint={breakpoint}
            onChange={handleAlignmentChange}
            stretched ={stretched}
            widget ={menuContainer}
          />
        </Property>
        <Property  nameStyle={styles.propertyName} className={styles.buttonAlignmentProperty}>
          <MenuContainerVerticalAlignment
            value={verticalAlignment}
            maxMargin={maxVerticalMargin}
            onChange={handleVerticalAlignmentChange}
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const MenuWidgetMobileAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const isGroupContainer = useGroupContainerSelector(editorSession, props);
  const template = _.get(props, 'template.widget') != null;
  const id = _.get(props, 'uid');
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';
  const widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`, []));

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const hasMenuContainer = useMemo(() => {
    const children = widgets.filter(item => item.parent === props.uid);
    return children.some(item => item.type === MENU_CONTAINER);
  }, [widgets, props.uid]);

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      {/* {!template && !isGroupContainer &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={() => changeView('liveElements')}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      } */}
      <Property.Divider />
      {hasMenuContainer &&
        <>
          <Property.Action className={styles.iconButton} tooltip='Alignment' onClick={() => changeView('style')}>
            <i className={cx("cleverstory-customize", styles.menuIcons)} />
          </Property.Action>
          <Property.Divider />
        </>
      }
      {allowLiveElement && 
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={() => changeView('liveElements')}>
          <i className={cx("cleverstory-live-element", styles.menuIcons)} />
        </Property.Action>
      }
      {!template && brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {view === 'style' &&
        <MenuStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'MENUS'} widgets={[props]} onCancel={() => closePopup()} />
      }
    </>
  );
}

const MenuWidget = React.forwardRef((props, ref) => {
  return <MenuWidgetView {...props} ref={ref} />
});

export const StretchMenuDialog = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widget, `properties.desktop`, {});
  const padding = _.get(properties, 'padding', 0);
  const stretched = _.get(properties, 'stretched', false);
  const layout = _.get(widget, `properties.${breakpoint}.layout`);

  const pageWidget = usePageWidgetSelector(editorSession, widget.uid);
  const pageWidth = _.get(pageWidget, 'properties.desktop.container.minWidth');

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, widget.uid, 'desktop', value));
  }

  const handleSectionSize = (stretched) => {
    var frame = new Frame(layout);
    const rect = getBoundingRectFromLayout(layout);

    if (stretched) {
      frame.remove('width');
      frame.set('transform', 'translate', `0px, ${rect.y}px`);
    } else {
      frame.set('width', `${pageWidth}px`);
      frame.set('transform', 'translate', `0px, ${rect.y}px`);
    }

    handlePropertyChange({
      stretched: stretched,
      layout: frame.get()
    });
  }

  return (
    <Property.Dialog className={styles.stretchSectionDialog}
      width={246}
      target={`#${id}`}
      title={'Stretch Menu'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property className={styles.sectionSizeProperty}>
          <div className={styles.sectionSizeContainer}>
            <div className={cx(styles.sectionSize, styles.stretched, {
              [styles.active]: stretched
            })} onClick={() => handleSectionSize(true)}>
              <div className={styles.icon}>
                <div className={styles.inner}></div>
              </div>
              <label>Screen</label>
            </div>
            <div className={cx(styles.sectionSize, {
              [styles.active]: !stretched
            })} onClick={() => handleSectionSize(false)}>
              <div className={styles.icon}>
                <div className={styles.inner}></div>
              </div>
              <label>Page</label>
            </div>
          </div>
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const MenuContainerHorizontalAlignment = ({ value, breakpoint, onChange, stretched, widget }) => {
  var containerWidth = _.get(widget, `properties.${breakpoint}.layout.width`);
  if (containerWidth !== "auto") {
    containerWidth = parseFloat(containerWidth);
  }  const position = _.get(value, 'position') || 'center';
  let margin = _.get(value, 'margin', 0);
  const pageWidth = window.innerWidth - 22;
  let translateX = 0, translateY = 0;
  if (widget.properties[breakpoint].layout?.transform) {
    const transform = widget.properties[breakpoint].layout.transform;
    if (!transform.translate){
      transform.translate = '0px,0px';
    }
    [translateX, translateY] = transform.translate.split(",").map(val => 
    Math.abs(parseFloat(val.replace("px", "").trim())));
    margin = parseFloat(margin) + translateX;
  }
  var maxRange;
  if (stretched) {
    maxRange = containerWidth === "auto" ? pageWidth - 261 : pageWidth - containerWidth - 5;
  } else {
    maxRange = (pageWidth >= 1287 && containerWidth === "auto") ? 942 : (containerWidth === "auto") ? pageWidth - 261 : (pageWidth <= 1130 && containerWidth !== "auto") ? 
    pageWidth - containerWidth - 5 : 1195 - containerWidth;
  }
  const handleAlignmentChange = (position) => {
    var request = {
      position: position
    }
    const transform = widget.properties[breakpoint].layout.transform;
    if (transform.translate) {
      transform.translate = '0px,0px';
    }
    if (position === 'right' || position === 'left') {
      request = {
        ...request,
        margin: breakpoint === 'desktop' ? 5 : 15
      }
    }

    onChange(request);
  }

  const handleMarginChange = (margin) => {
    var request = {
      margin: margin
    }
    const transform = widget.properties[breakpoint].layout.transform;
    if (transform.translate) {
      transform.translate = '0px,0px';
    }
    onChange(request);
  }

  return (
    <div className={styles.buttonAlignment}>
      <span className={styles.subMenuName}>Horizontal</span>
      <div className={styles.options}>
        <Tooltip placement="top" content="Left Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'left'
            })}
            onClick={handleAlignmentChange.bind(this, 'left')}
          >
            <i className={cx('cleverstory-widgets-align-left')}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Center Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'center'
            })}
            onClick={handleAlignmentChange.bind(this, 'center')}
          >
            <i className={cx('cleverstory-widgets-align-center')}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Right Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'right'
            })}
            onClick={handleAlignmentChange.bind(this, 'right')}
          >
            <i className={cx('cleverstory-widgets-align-right')}></i>
          </button>
        </Tooltip>
      </div>


      {position !== 'center' &&
        <Property name="Margin" className={styles.marginProperty}>
          <Property.Slider
            input
            min={breakpoint === 'desktop' ? 5 : 15}
            max={breakpoint === 'desktop' ? maxRange : 360 - containerWidth} 
            step={1}
            value={margin}
            onChange={handleMarginChange}
          />
        </Property>
      }
    </div>
  )
}

const MenuContainerVerticalAlignment = ({ value, maxMargin, onChange }) => {

  const position = _.get(value, 'position') || 'center';
  const margin = _.get(value, 'margin', 0);

  const handleAlignmentChange = (position) => {
    var request = {
      position: position
    }

    if (position === 'top' || position === 'bottom') {
      request = {
        ...request,
        margin: 5 
      }
    }

    onChange(request);
  }

  const handleMarginChange = (margin) => {
    if( margin > maxMargin) {
      return
    }

    var request = {
      margin: margin
    }

    onChange(request);
  }

  return (
    <div className={styles.buttonAlignment}>
      <span className={styles.subMenuName}>Vertical</span>
      <div className={styles.options}>
        <Tooltip placement="top" content="Top Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'top'
            })}
            onClick={handleAlignmentChange.bind(this, 'top')}
          >
            <i className={cx('cleverstory-widgets-align-top')}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Center Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'center'
            })}
            onClick={handleAlignmentChange.bind(this, 'center')}
          >
            <i className={cx('cleverstory-widgets-align-center')}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Bottom Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'bottom'
            })}
            onClick={handleAlignmentChange.bind(this, 'bottom')}
          >
            <i className={cx('cleverstory-widgets-align-bottom')}></i>
          </button>
        </Tooltip>
      </div>


      {position !== 'center' &&
        <Property name="Margin" className={styles.marginProperty}>
          <Property.Slider
            input
            min={5}
            max={maxMargin}
            step={1}
            value={margin}
            onChange={handleMarginChange}
          />
        </Property>
      }
    </div>
  )
}

const ScrollEffectDialog = ({ id, editor, widget, onCancel }) => {
  const dispatch = useDispatch();

  const scrollEffect = _.get(widget, `properties.desktop.scrollEffect`, 'NONE');

  const menuEffects = SCROLL_EFFECTS.filter(effect => effect.section === 'menu');
  const backgroundEffects = SCROLL_EFFECTS.filter(effect => effect.section === 'background');

  const handlePropertyChange = (value) => {
    var request = {
      scrollEffect: value
    }
    dispatch(updateWidgetProperty(editor, id, 'desktop', request));
  }

  return (
    <Property.Dialog className={styles.scrollEffectDialog}
      width={279}
      target={`#${id}`}
      title={'Scroll Effects'}
      onCancel={onCancel}
    >
      <Property.Body>
        <Property className={styles.scrollEffectProperty}>
          <span className={styles.subMenuName}>Entire Menu</span>
          <div className={styles.optionContainer}>
            {menuEffects.map((effects, index) => {
              return (
                <ScrollAnimationCard
                  effects={effects}
                  index={index}
                  handlePropertyChange={handlePropertyChange}
                  scrollEffect={scrollEffect}
                />
              )
            })
            }
          </div>
        </Property>
        <Property className={styles.scrollEffectProperty}>
          <span className={styles.subMenuName}>Only Menu Background</span>
          <div className={styles.optionContainer}>
            {backgroundEffects.map((effects, index) => {
              return (
                <ScrollAnimationCard
                  effects={effects}
                  index={index}
                  handlePropertyChange={handlePropertyChange}
                  scrollEffect={scrollEffect}
                />
              )
            })
            }
          </div>
        </Property>
      </Property.Body>
    </Property.Dialog>
  )
}

const ScrollAnimationCard = ({ index, effects, scrollEffect, handlePropertyChange }) => {

  const [stopped, setStopped] = useState(true)

  const options = {
    loop: false,
    autoplay: false,
    animationData: require(`assets/images/scrollEffects/${effects.type.toLowerCase()}.json`),
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice"
    }
  };

  return (
    <div
      key={index}
      onMouseEnter={() => { setStopped(false) }}
      onMouseLeave={() => { setStopped(true) }}
      onClick={() => { handlePropertyChange(effects.type) }}
      className={cx(styles.scrollEffectCard)}>
      <div className={cx(styles.effectOverview, { [styles.active]: scrollEffect === effects.type })}>
        <Lottie options={options} isClickToPauseDisabled isStopped={stopped} />
      </div>
      <span className={styles.effectsTitle}>{effects.name}</span>
    </div>
  )
}

MenuWidget.actions = (editorSession, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <MenuWidgetDesktopAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
      {breakpoint === 'mobile' &&
        <MenuWidgetMobileAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

MenuWidget.processRequest = (widget) => {
  var background = _.get(widget, 'properties.desktop.background');

  if (!background) {
    return widget;
  }

  var media = _.get(background, 'media');
  if (media) {
    background = {
      ...background,
      media: media.id
    }
  }

  return {
    ...widget,
    properties: {
      ...widget.properties,
      desktop: {
        ...widget.properties.desktop,
        background: background
      }
    }
  }
}

MenuWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

MenuWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  const desktop = breakpoint === 'desktop';

  return {
    draggable: true,
    resizable: true,
    rotatable: false,
    cloneable: true,
    renderDirections: ['n', 's'],
    onDrag: (layout, widget) => {
      const frame = new Frame(layout);

      // current translate string from the layout (e.g. "0px, 12px" or "translate3d(0px, 12px, 0)")
      const translate = frame.get("transform", "translate") || "";
      // get the Y value safely
      const values = _.split(translate.replace(/translate3d|translateY|\(|\)|translate/g, ""), ",").map(v => v && v.trim());
      const parsedY = parseFloat(values[1]) || 0;
    
      const documentElement = document.querySelector('[data-storyboard-type]');
      const storyboardType = documentElement?.getAttribute('data-storyboard-type');
    
      // clamp y for certain storyboard types
      let y = parsedY;
      if (PAGE_CONTAIN_STORYBOARDS.includes(storyboardType)) {
        y = Math.max(0, y);
      }
    
      const element = document.getElementById(widget);
      if (!element) {
        return frame.get();
      }
    
      const rect = element.getBoundingClientRect();
      const childElement = element.querySelector('[data-widget-type]');
      const elementType = childElement.getAttribute('data-widget-type');

      if (!childElement) {
        return frame.get();
      }

      if(elementType === MENU_DRAGGABLE_CONTAINER) {

        const childRect = childElement.getBoundingClientRect();
      
        // child bottom relative to parent's top (in px)
        const childRelativeTop = (childRect.top - rect.top);
        const childRelativeBottom = childRelativeTop + childRect.height; // how far down from parent's top
      
        // optional padding so child isn't flush to the bottom
        const padding = 0; // adjust if you want some breathing room
      
        // compute required height to fully contain the child
        const requiredHeight = Math.ceil(childRelativeBottom + padding);
      
        // respect the currently set height and any minimum (do not shrink below requiredHeight)
        const currentHeight = parseFloat(frame.get("height")) || rect.height || 0;
        const specifiedMinHeight = 0; // set if you have a minHeight from props/state (e.g. containerMinWidth equivalent)
        const finalHeight = Math.max(currentHeight, requiredHeight, specifiedMinHeight);
      
        // set new height only if it actually needs to grow
        if (finalHeight !== currentHeight) {
          frame.set("height", `${finalHeight}px`);
        }
      }
      
      // set translate (keep X as 0)
      const newTranslate = `0px, ${Math.round(y)}px`;
      frame.set("transform", "translate", newTranslate);
    
      return frame.get();
    },
    onResize: (widget, layout, breakpoint) => {
      const stretched = _.get(widget, 'properties.desktop.stretched');
      const frame = new Frame(layout);

      if (stretched && breakpoint === 'desktop') {
        const margin = _.get(widget, 'properties.desktop.margin', 0);
        const width = `calc(var(--editor-width) - 2 * ${margin}px)`;
        frame.set('width', width);
      }

      return frame.get();
    },
    onReparent: (widget, parent) => {
      const type = getWidgetType(editorSession, parent)
      if (type === PAGE) return true;
      return false;
    }
  }
}

function getStyles(widgetProps, breakpoint) {
  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false);
  const margin = _.get(widgetProps, 'properties.desktop.margin', 0);
  const padding = _.get(widgetProps, `properties.${breakpoint}.padding`, 0);

  var widgetStyles = {
    marginLeft: 0,
    paddingTop: `${padding}px`,
    paddingBottom: `${padding}px`
  }

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      paddingLeft: `${padding}px`,
      width: '100%',
      paddingRight: `${padding}px`
    }

    if (stretched) {
      widgetStyles = {
        ...widgetStyles,
        width: `calc(var(--editor-width) - 2 * ${margin}px) !important`,
        left: `calc((100% - (var(--editor-width) - 2 * ${margin}px))/2)`
      }
    }
  }

  if (breakpoint === 'mobile') {
    widgetStyles = {
      ...widgetStyles,
    }
  }

  return widgetStyles;
}

function getContainerAlignment(widgetProps, breakpoint) {
  const alignment = _.get(widgetProps, `properties.${breakpoint}.alignment`, {
    position: 'center',
    margin: 0
  });

  const verticalAlignment = _.get(widgetProps, `properties.${breakpoint}.verticalAlignment`, {
    position: 'center',
    margin: 0
  });

  var position = _.get(alignment, 'position', 'center');
  var margin = _.get(alignment, 'margin', 0);

  var verticalPosition = _.get(verticalAlignment, 'position', 'center');
  var verticalMargin = _.get(verticalAlignment, 'margin', 0);

  var containerStyles = {}

  //alignment
  containerStyles['justifyContent'] = position;

  if (position === 'left') {
    containerStyles = {
      ...containerStyles,
      justifyContent: 'flex-start',
      paddingLeft: `${margin}px`
    }
  } else if (position === 'right') {
    containerStyles = {
      ...containerStyles,
      justifyContent: 'flex-end',
      paddingRight: `${margin}px`
    }
  }

  //verticalAlignment
  containerStyles['alignItems'] = verticalPosition;

  if (verticalPosition === 'top') {
    containerStyles = {
      ...containerStyles,
      alignItems: 'flex-start',
      paddingTop: `${verticalMargin}px`
    }
  }
  else if (verticalPosition === 'bottom') {
    containerStyles = {
      ...containerStyles,
      alignItems: 'flex-end',
      paddingBottom: `${verticalMargin}px`
    }
  }

  return containerStyles;
}

export { MenuWidget };